/**
 * A workspace's URL slug changed (Settings → Workspace, or `workspace.updated` from another admin). The shell keeps
 * its tabs per slug (`bundle.shell.v1.<slug>`), so before the app switches to the new slug the saved tabs move over
 * with their URLs rewritten, and the address bar is rewritten in place: the open tabs (and this settings page) stay.
 */
import type { KeyValueStorage } from "@bundle/core";

const SHELL_KEY = "bundle.shell.v1.";

function rewrite(url: string, from: string, to: string): string {
  const prefix = `/${from}`;
  if (url === prefix) return `/${to}`;
  return url.startsWith(`${prefix}/`) || url.startsWith(`${prefix}?`) ? `/${to}${url.slice(prefix.length)}` : url;
}

export function moveWorkspaceSlug(storage: KeyValueStorage, from: string, to: string): void {
  if (from === to) return;
  const raw = storage.get(`${SHELL_KEY}${from}`);
  if (raw !== undefined && storage.get(`${SHELL_KEY}${to}`) === undefined) {
    try {
      const moved = JSON.parse(raw, (_k, v: unknown) => (typeof v === "string" ? rewrite(v, from, to) : v)) as unknown;
      storage.set(`${SHELL_KEY}${to}`, JSON.stringify(moved));
      storage.remove(`${SHELL_KEY}${from}`);
    } catch {
      // Unreadable: the new slug starts with a fresh shell.
    }
  }
  const path = rewrite(location.pathname, from, to);
  if (path !== location.pathname) history.replaceState(history.state, "", `${path}${location.search}${location.hash}`);
}
